import { Trash2 } from "lucide-react";
import {
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { cx } from "../../lib/cx";
import { impact } from "../../native";
import { HOLD_MS, type HoldEnd, holdEnd, isCancelArmed } from "./hold-decision";

export type { HoldEnd } from "./hold-decision";
export { isGhostClick } from "./hold-decision";

export interface HoldToRecordProps {
  className?: string;
  /**
   * Stamped with `Date.now()` whenever a gesture finishes — a tap or a hold.
   * The recording controls ("Cancelar", "Parar") pass it to `isGhostClick`
   * and ignore a click inside 450 ms: the browser synthesises a click for the
   * touch that just ended, and it lands exactly where "Parar" appears.
   */
  guardRef?: RefObject<number>;
  /** The icon at rest (the mic). A slide that arms cancel shows a bin. */
  idleIcon: ReactNode;
  /** The button's accessible name. */
  label: string;
  /**
   * The slide crossed (or came back inside) the cancel threshold. The caller
   * draws the armed state OUTSIDE the button — the hint above the composer
   * reads "Solte para cancelar" — while `data-phase` stays the truth here.
   * Called with `false` when the gesture ends armed.
   */
  onArmedChange?: (armed: boolean) => void;
  /** Recording ended: sent, too short to send, or thrown away. */
  onHoldEnd: (end: HoldEnd) => void;
  /** The press lasted 220 ms: start recording. A haptic has fired. */
  onHoldStart: () => void;
  /** A tap: listen until stopped. */
  onTapListen: () => void;
  /** A tooltip, e.g. "Toque para transcrever · segure para gravar e enviar". */
  title?: string;
}

type Phase = "idle" | "holding" | "armed";

interface Handlers {
  onArmedChange?: (armed: boolean) => void;
  onHoldEnd: (end: HoldEnd) => void;
  onHoldStart: () => void;
  onTapListen: () => void;
}

/**
 * The mic button's gesture, from A2's `coach.tsx:229-276, 480-545`. A TAP
 * listens until stopped; HOLDING 220 ms records, and letting go sends;
 * sliding more than 72 px left first throws it away; a recording released
 * under 700 ms after the press is `tooShort`, and the caller says "Segure
 * para gravar o áudio". Recording itself is not here (T62).
 *
 * ── THE LISTENERS ARE ON `window`, ON PURPOSE ──────────────────────────────
 * Move, up and cancel are added to `window` on pointerdown, not to the
 * button. The button re-renders the moment recording starts — and the caller
 * may swap it for another element — and pointer capture does not survive
 * that. `setPointerCapture` looks like the obvious fix for "the finger left
 * the button" and is wrong here for that reason. Do not simplify it.
 *
 * ── THE GHOST CLICK ────────────────────────────────────────────────────────
 * Every finish stamps `guardRef`; see `isGhostClick`.
 *
 * `onContextMenu` is prevented: a long press on Android opens a menu.
 * `touch-none select-none`: the WebView must not scroll or select under it.
 */
export function HoldToRecord({
  className,
  guardRef,
  idleIcon,
  label,
  onArmedChange,
  onHoldEnd,
  onHoldStart,
  onTapListen,
  title,
}: HoldToRecordProps) {
  const [phase, setPhase] = useState<Phase>("idle");

  // The callbacks as of the latest render: a gesture outlives the render it
  // started in, and must call the caller's CURRENT handlers when it ends. A
  // caller that rebuilds `onArmedChange` every render must not re-subscribe
  // the window listeners, so it is read here and never in a dependency array.
  const handlers = useRef<Handlers>({
    onArmedChange,
    onHoldEnd,
    onHoldStart,
    onTapListen,
  });
  useLayoutEffect(() => {
    handlers.current = { onArmedChange, onHoldEnd, onHoldStart, onTapListen };
  });

  // Removes the window listeners and the timer of a gesture in flight.
  const teardown = useRef<(() => void) | null>(null);
  useEffect(() => () => teardown.current?.(), []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button > 0 || !event.isPrimary || teardown.current !== null) {
        return;
      }
      const { pointerId, clientX: startX } = event;
      const at = Date.now();
      let recording = false;
      let cancelArmed = false;

      const onMove = (move: PointerEvent) => {
        if (move.pointerId !== pointerId || !recording) {
          return;
        }
        const armed = isCancelArmed(move.clientX - startX);
        if (armed !== cancelArmed) {
          cancelArmed = armed;
          setPhase(armed ? "armed" : "holding");
          handlers.current.onArmedChange?.(armed);
        }
      };

      const finish = (interrupted: boolean) => {
        teardown.current?.();
        if (guardRef) {
          guardRef.current = Date.now();
        }
        setPhase("idle");
        if (cancelArmed) {
          // The caller's copy of the armed state outlives this button's own
          // `phase`, so it is told the gesture left it. `cancelArmed` itself
          // stays true: `holdEnd` below is what throws the recording away.
          handlers.current.onArmedChange?.(false);
        }
        if (!recording) {
          // A tap. A pointer the browser took back before the hold began is
          // neither a tap nor a recording: nothing happens.
          if (!interrupted) {
            handlers.current.onTapListen();
          }
          return;
        }
        handlers.current.onHoldEnd(
          holdEnd({ heldMs: Date.now() - at, cancelArmed, interrupted })
        );
      };

      const onUp = (up: PointerEvent) => {
        if (up.pointerId === pointerId) {
          finish(false);
        }
      };
      const onCancel = (cancel: PointerEvent) => {
        if (cancel.pointerId === pointerId) {
          finish(true);
        }
      };

      const timer = window.setTimeout(() => {
        recording = true;
        setPhase("holding");
        impact("medium");
        handlers.current.onHoldStart();
      }, HOLD_MS);

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancel);
      teardown.current = () => {
        window.clearTimeout(timer);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancel);
        teardown.current = null;
      };
    },
    [guardRef]
  );

  const onContextMenu = useCallback(
    (event: ReactMouseEvent) => event.preventDefault(),
    []
  );

  return (
    <button
      aria-label={label}
      className={cx(
        // `*:pointer-events-none`: the finger's target must be the BUTTON, never
        // the icon. The icon is swapped (mic → bin) mid-gesture, and a touch
        // stays targeted at the node it began on: once that node is detached,
        // its pointerup never bubbles to the window listeners.
        "grid size-12 shrink-0 touch-none select-none place-items-center rounded-full transition-[background-color,transform] *:pointer-events-none",
        phase === "idle" && "a-press bg-inverse text-on-inverse",
        phase === "holding" && "a-primary scale-110 shadow-ember",
        phase === "armed" && "scale-110 bg-accent-soft text-accent-ink",
        className
      )}
      data-phase={phase}
      onContextMenu={onContextMenu}
      onPointerDown={onPointerDown}
      title={title}
      type="button"
    >
      {phase === "armed" ? (
        <Trash2 aria-hidden="true" className="size-5" strokeWidth={2.25} />
      ) : (
        idleIcon
      )}
    </button>
  );
}
